import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { useLeague } from '../utils/LeagueContext';
import API_BASE_URL from '../utils/apiConfig'; // Import API base URL


const GoogleLoginButton = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false); // Add loading state for UI feedback
  const { setSelectedLeagueAndId } = useLeague();

  const handleLoginSuccess = async (response) => {
    if (!response || !response.credential) {
      console.error('No credential in response');
      setError('Failed to retrieve token from Google');
      return;
    }
    setLoading(true); // Start loading when making the API request

    try {
      // Send the Google token to the backend for validation
      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',  // Make sure cookies are sent along with requests
        body: JSON.stringify({ token: response.credential }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error('Backend Error:', errorData);
        setError(errorData.message || 'Failed to log in with Google');
        return;
      }

      const data = await res.json();

      // If the login is successful, save the user information in state or context
      if (data.user && data.token) {
        // Merge token into the user object
        const userWithToken = {
          ...data.user,
          token: data.token,
        };
        
        // Save the merged object (user + token) to localStorage
        localStorage.setItem('user', JSON.stringify(userWithToken));
        if (data.leagues) {
          localStorage.setItem("leagues", JSON.stringify(data.leagues));  // Save leagues data
        }
        
        // Update context with the merged user object
        setUser(userWithToken);

        // Select enriched league BEFORE navigating
        const enrichedLeague = data.leagues.find(
          l => l.inviteStatus === 'active' || l.inviteStatus === 'invited'
        );

        if (enrichedLeague) {
          await setSelectedLeagueAndId(
            enrichedLeague.leagueId,
            enrichedLeague
         );
        } else {
          console.warn('No eligible league found during login');
        }
        
        // Redirect the user to the dashboard (or any other page)
        // Get the original location from state or default to a page
        const from = location.state?.from?.pathname || '/commissionerdash';
        // After successful login:
        navigate(from, { replace: true });
      } else {
        setError(data.message || 'Oops, we had a problem logging you in. Please try again.');
        console.error('Login failed:', data.message);
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError('Oops, we had a problem logging you in. Please try again.');
    } finally {
      setLoading(false); // End loading
    }
  };

  const handleLoginFailure = (error) => {
    console.error('Login Failed:', error);
    setError('Login failed. Please try again.');
  };

  return (
    <div>
      <GoogleLogin
        onSuccess={handleLoginSuccess}
        onError={handleLoginFailure}
      />
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>Loading...</p>} {/* Show loading state */}
    </div>
  );
};

export default GoogleLoginButton;
